/**
 * The alert bar at the top of the operator screen (Marina-style): the
 * tenant's active alerts about this production's studio and site, most
 * severe first (`lib/playout/alertBar.ts` chooses them). Each can be hidden
 * "for me" — this browser only, until it is raised again — or acknowledged
 * "for everyone" (`POST /alerts/acknowledge`, audited; needs `playout.take`
 * on the alert's place). Three show at once; the rest behind "N more".
 *
 * Nothing renders when there is nothing to show.
 */
import type { AlertWire } from '@norna/api-contract/alerts.js';
import { BellOffIcon, CheckCheckIcon, TriangleAlertIcon } from 'lucide-react';
import { useState } from 'react';
import { alertLabel, severityTone } from '../../lib/alerts.js';
import { cn } from '../../lib/utils.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';

export type AlertBarProps = {
  readonly alerts: readonly AlertWire[];
  /** The alerts the user may acknowledge for everyone (by key). */
  readonly acknowledgeable: ReadonlySet<string>;
  /** Acknowledgements in flight (by key). */
  readonly pending: ReadonlySet<string>;
  readonly onHide: (alert: AlertWire) => void;
  readonly onAcknowledge: (alert: AlertWire) => void;
};

/** How many show before "N more". */
export const BAR_ROWS = 3;

function since(iso: string): string {
  return iso.slice(11, 16);
}

export function AlertBar({ alerts, acknowledgeable, pending, onHide, onAcknowledge }: AlertBarProps) {
  const [expanded, setExpanded] = useState(false);
  if (alerts.length === 0) return null;
  const shown = expanded ? alerts : alerts.slice(0, BAR_ROWS);
  const hiddenCount = alerts.length - shown.length;
  return (
    <section aria-label="Alerts" className="grid border-b" data-testid="alert-bar">
      {shown.map((alert) => {
        const canAck = acknowledgeable.has(alert.key);
        const acking = pending.has(alert.key);
        return (
          <div
            key={alert.key}
            role={alert.severity === 'critical' ? 'alert' : 'status'}
            className={cn('flex flex-wrap items-center gap-2 border-l-4 px-4 py-1.5 text-sm', alert.severity === 'critical' ? 'border-l-offline bg-offline/10' : alert.severity === 'warning' ? 'border-l-warning bg-warning/10' : 'border-l-border')}
            data-testid="alert-bar-row"
            data-alert-key={alert.key}
            data-severity={alert.severity}
          >
            <TriangleAlertIcon className="size-4 shrink-0" aria-hidden="true" />
            <Badge variant={severityTone(alert.severity)}>{alert.severity}</Badge>
            <span className="font-medium">{alertLabel(alert.type)}</span>
            <span className="min-w-0 flex-1 truncate text-muted-foreground" title={alert.summary}>
              {alert.summary}
            </span>
            <span className="font-mono text-xs text-muted-foreground tabular-nums" title={alert.raisedAt}>
              since {since(alert.raisedAt)}
            </span>
            <Button size="sm" variant="ghost" onClick={() => onHide(alert)} aria-label={`Hide for me: ${alertLabel(alert.type)}`} data-testid="alert-hide">
              <BellOffIcon />
              For me
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!canAck || acking}
              title={canAck ? 'Acknowledge for everyone: every operator screen hides it, and the history records you' : 'Acknowledging for everyone needs playout.take where the alert is'}
              onClick={() => onAcknowledge(alert)}
              aria-label={`Acknowledge for everyone: ${alertLabel(alert.type)}`}
              data-testid="alert-ack"
            >
              <CheckCheckIcon />
              {acking ? 'Acknowledging…' : 'For everyone'}
            </Button>
          </div>
        );
      })}
      {hiddenCount > 0 || expanded ? (
        <Button size="sm" variant="ghost" className="justify-start rounded-none px-4" onClick={() => setExpanded(!expanded)} data-testid="alert-bar-more">
          {expanded ? 'Show fewer' : `${String(hiddenCount)} more alert${hiddenCount === 1 ? '' : 's'}`}
        </Button>
      ) : null}
    </section>
  );
}
